// export function getSalesTransactionGrowthChartData({
//   transactions,
//   selectedStoreCode,
//   selectedMonthName,
//   selectedYear,
// }) {
//   // 1) filter transactions
//   const filtered = (transactions || [])
//     .filter((tx) => tx.status === "C")
//     .filter((sale) => {
//       const date = new Date(sale.tran_date);
//       const year = date.getFullYear();
//       const month = date.toLocaleString("default", { month: "long" });
//       const store = sale.store_code.replace("ANGELSPIZZA_", "");

//       const okYear = selectedYear ? year === parseInt(selectedYear, 10) : true;
//       const okMonth = selectedMonthName ? month === selectedMonthName : true;
//       const okStore = selectedStoreCode ? store === selectedStoreCode : true;
//       return okYear && okMonth && okStore;
//     });

//   // 2) group by monthYear
//   const salesByMonth = {};
//   const txCountByMonth = {};
//   filtered.forEach((sale) => {
//     const d = new Date(sale.tran_date);
//     const year = d.getFullYear();
//     const month = d.toLocaleString("default", { month: "long" });
//     const key = `${month} ${year}`;

//     salesByMonth[key] = salesByMonth[key] || [];
//     txCountByMonth[key] = txCountByMonth[key] || 0;

//     salesByMonth[key].push(sale);
//     txCountByMonth[key]++;
//   });

//   // 3) calculate totals and counts
//   const labels = Object.keys(salesByMonth);
//   const grossSales = labels.map((key) => {
//     return salesByMonth[key].reduce((sum, sale) => {
//       // parse numeric fields
//       const totalVal = parseFloat(sale.total) || 0;
//       // use totalVal as gross sales
//       return sum + totalVal;
//     }, 0);
//   });
//   const txCounts = labels.map((key) => txCountByMonth[key]);

//   // 4) return chart data (keeping original structure for print table compatibility)
//   return {
//     labels,
//     datasets: [
//       {
//         type: "line",
//         label: "Gross Sales",
//         data: grossSales,
//         borderColor: "#ffd043",
//         borderWidth: 2,
//         fill: false,
//         yAxisID: "y-axis-2",
//       },
//       {
//         type: "bar",
//         label: "Total Transactions",
//         data: txCounts,
//         backgroundColor: "#006096",
//         yAxisID: "y-axis-1",
//       },
//     ],
//   };
// }

export function getSalesTransactionGrowthChartData({
  transactions,
  selectedStoreCode,
  selectedMonthName,
  selectedYear, // current year selected (e.g. "2026")
  includePreviousYear = true,
}) {
  const currentYear = selectedYear ? parseInt(selectedYear, 10) : null;
  const previousYear = currentYear ? currentYear - 1 : null;

  // helper
  const getStore = (store_code) =>
    String(store_code || "").replace("ANGELSPIZZA_", "");
  const getMonth = (date) => date.toLocaleString("default", { month: "long" });

  // 1) filter: DO NOT lock to currentYear only if includePreviousYear is true
  const filtered = (transactions || [])
    .filter((tx) => tx.status === "C")
    .filter((sale) => {
      const date = new Date(sale.tran_date);
      const year = date.getFullYear();
      const month = getMonth(date);
      const store = getStore(sale.store_code);

      const okStore = selectedStoreCode ? store === selectedStoreCode : true;
      const okMonth = selectedMonthName ? month === selectedMonthName : true;

      // ✅ include previous year same month
      let okYear = true;
      if (currentYear) {
        if (includePreviousYear && previousYear) {
          okYear = year === currentYear || year === previousYear;
        } else {
          okYear = year === currentYear;
        }
      }

      return okStore && okMonth && okYear;
    });

  // 2) aggregate by month-year (key = "January 2025")
  const salesByKey = {};
  const txCountByKey = {};

  filtered.forEach((sale) => {
    const d = new Date(sale.tran_date);
    const year = d.getFullYear();
    const month = getMonth(d);
    const key = `${month} ${year}`;

    salesByKey[key] = (salesByKey[key] || 0) + (parseFloat(sale.total) || 0);
    txCountByKey[key] = (txCountByKey[key] || 0) + 1;
  });

  // 3) build labels in correct order (prev then current)
  const labels = [];
  if (selectedMonthName && previousYear)
    labels.push(`${selectedMonthName} ${previousYear}`);
  if (selectedMonthName && currentYear)
    labels.push(`${selectedMonthName} ${currentYear}`);

  // fallback if not specific month/year
  if (!labels.length) {
    labels.push(
      ...Object.keys(salesByKey).sort((a, b) => {
        // sort by year then month index
        const [ma, ya] = a.split(" ");
        const [mb, yb] = b.split(" ");
        const da = new Date(`${ma} 1, ${ya}`).getTime();
        const db = new Date(`${mb} 1, ${yb}`).getTime();
        return da - db;
      }),
    );
  }

  const grossSales = labels.map((k) => Number(salesByKey[k] || 0));
  const txCounts = labels.map((k) => Number(txCountByKey[k] || 0));

  // 4) comparison object (needed for 2-bar under same month)
  const previousYearLabel =
    selectedMonthName && previousYear
      ? `${selectedMonthName} ${previousYear}`
      : null;
  const currentYearLabel =
    selectedMonthName && currentYear
      ? `${selectedMonthName} ${currentYear}`
      : null;

  const comparison =
    previousYearLabel && currentYearLabel
      ? {
          previousYear,
          currentYear,
          previousYearData: {
            labels: [previousYearLabel],
            grossSales: [Number(salesByKey[previousYearLabel] || 0)],
            txCounts: [Number(txCountByKey[previousYearLabel] || 0)],
          },
          currentYearData: {
            labels: [currentYearLabel],
            grossSales: [Number(salesByKey[currentYearLabel] || 0)],
            txCounts: [Number(txCountByKey[currentYearLabel] || 0)],
          },
        }
      : null;

  return {
    labels,
    datasets: [
      {
        type: "line",
        label: "Gross Sales",
        data: grossSales,
        borderColor: "#ffd043",
        borderWidth: 2,
        fill: false,
        yAxisID: "y-axis-2",
      },
      {
        type: "bar",
        label: "Total Transactions",
        data: txCounts,
        backgroundColor: "#006096",
        yAxisID: "y-axis-1",
      },
    ],
    comparison, // ✅ added
  };
}

/**
 * Build Highcharts options for dual-axis Gross Sales vs Transactions chart.
 *
 * @param {{ labels: string[], datasets: object[] }} chartData
 * @returns {object} Highcharts configuration object
 */
// export function getSalesTransactionGrowthChartOptions(chartData) {
//   const { labels = [], datasets = [] } = chartData || {};
//   const grossSalesData = datasets[0]?.data || [];
//   const txCountsData = datasets[1]?.data || [];

//   return {
//     chart: {
//       zoomType: "xy",
//     },
//     title: {
//       text: null,
//     },
//     xAxis: [
//       {
//         categories: labels,
//         crosshair: true,
//       },
//     ],
//     yAxis: [
//       {
//         // Primary yAxis (left) - Total Transactions
//         title: {
//           text: "Total Transactions",
//           style: { color: "#006096" },
//         },
//         labels: {
//           format: "{value:.0f}",
//           style: { color: "#006096" },
//         },
//         allowDecimals: false,
//       },
//       {
//         // Secondary yAxis (right) - Gross Sales
//         title: {
//           text: "Gross Sales",
//           style: { color: "#ffd043" },
//         },
//         labels: {
//           format: "{value:,.0f}",
//           style: { color: "#ffd043" },
//         },
//         opposite: true,
//       },
//     ],
//     tooltip: {
//       shared: true,
//       useHTML: true,
//       formatter: function () {
//         let tooltip = `<b>${this.x}</b><br/>`;
//         this.points.forEach((point) => {
//           let value;
//           if (point.series.name.includes("Transactions")) {
//             value = Math.round(point.y);
//           } else {
//             value = point.y.toLocaleString(undefined, {
//               maximumFractionDigits: 2,
//             });
//           }
//           tooltip += `<span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${value}</b><br/>`;
//         });
//         return tooltip;
//       },
//     },
//     legend: {
//       layout: "horizontal",
//       align: "center",
//       verticalAlign: "bottom",
//     },
//     series: [
//       {
//         name: "Total Transactions",
//         type: "column",
//         yAxis: 0,
//         data: txCountsData,
//         color: "#006096",
//       },
//       {
//         name: "Gross Sales",
//         type: "line",
//         yAxis: 1,
//         data: grossSalesData,
//         color: "#ffd043",
//         lineWidth: 2,
//         marker: {
//           enabled: true,
//           radius: 4,
//         },
//       },
//     ],
//     credits: {
//       enabled: false,
//     },
//   };
// }

export function getSalesTransactionGrowthChartOptions(chartData) {
  const comparison = chartData?.comparison;

  const safeNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const fmtMoney = (n) =>
    safeNum(n).toLocaleString(undefined, { maximumFractionDigits: 2 });

  // Fallback (no comparison) -> keep old behavior
  if (!comparison?.previousYearData || !comparison?.currentYearData) {
    const labels = chartData?.labels || [];
    const datasets = chartData?.datasets || [];
    const grossSales =
      datasets.find((d) => d.label === "Gross Sales")?.data || [];
    const txCounts =
      datasets.find((d) => d.label === "Total Transactions")?.data || [];

    return {
      chart: { type: "column", zoomType: "xy" },
      title: { text: null },
      xAxis: { categories: labels, crosshair: true },
      yAxis: [
        {
          title: { text: "Total Transactions", style: { color: "#006096" } },
          labels: { format: "{value:.0f}", style: { color: "#006096" } },
          allowDecimals: false,
        },
        {
          title: { text: "Gross Sales", style: { color: "#ffd043" } },
          labels: {
            formatter() {
              return Number(this.value).toLocaleString();
            },
            style: { color: "#ffd043" },
          },
          opposite: true,
        },
      ],
      tooltip: { shared: true },
      series: [
        {
          name: "Total Transactions",
          type: "column",
          yAxis: 0,
          data: txCounts.map(safeNum),
          color: "#006096",
        },
        {
          name: "Gross Sales",
          type: "column",
          yAxis: 1,
          data: grossSales.map(safeNum),
          color: "#ffd043",
        },
      ],
      credits: { enabled: false },
    };
  }

  const { previousYear, currentYear, previousYearData, currentYearData } =
    comparison;

  const monthFromLabel = (label) => String(label).split(" ")[0] || label;
  const month = currentYearData?.labels?.[0]
    ? monthFromLabel(currentYearData.labels[0])
    : previousYearData?.labels?.[0]
      ? monthFromLabel(previousYearData.labels[0])
      : "January";

  // ✅ X-axis: left = January 2025, right = January 2026
  const categories = [`${month} ${previousYear}`, `${month} ${currentYear}`];

  const txSeries = [
    safeNum(previousYearData?.txCounts?.[0]),
    safeNum(currentYearData?.txCounts?.[0]),
  ];

  const salesSeries = [
    safeNum(previousYearData?.grossSales?.[0]),
    safeNum(currentYearData?.grossSales?.[0]),
  ];

  return {
    chart: {
      type: "column",
      zoomType: "xy",
    },
    title: { text: null },

    xAxis: {
      categories,
      crosshair: true,
    },

    yAxis: [
      {
        // Left axis for Transactions
        min: 0,
        title: { text: "Total Transactions", style: { color: "#006096" } },
        labels: { format: "{value:.0f}", style: { color: "#006096" } },
        allowDecimals: false,
      },
      {
        // Right axis for Gross Sales
        title: { text: "Gross Sales", style: { color: "#ffd043" } },
        labels: {
          formatter: function () {
            return Number(this.value).toLocaleString();
          },
          style: { color: "#ffd043" },
        },
        opposite: true,
      },
    ],

    tooltip: {
      shared: true,
      useHTML: true,
      formatter: function () {
        let html = `<b>${this.x}</b><br/>`;
        this.points.forEach((p) => {
          const isTx = p.series.name === "Total Transactions";
          const val = isTx ? Math.round(p.y) : fmtMoney(p.y);
          html += `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${val}</b><br/>`;
        });
        return html;
      },
    },

    legend: {
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom",
    },

    plotOptions: {
      column: {
        grouping: true, // ✅ two bars per category
        borderWidth: 0,
        pointPadding: 0.08,
        groupPadding: 0.18,
      },
    },

    series: [
      {
        name: "Total Transactions",
        type: "column",
        yAxis: 0,
        data: txSeries,
        color: "#006096",
        dataLabels: {
          enabled: true,
          formatter: function () {
            return Math.round(this.y);
          },
          style: { fontWeight: "bold", color: "#ffffff", textOutline: "none" },
        },
      },
      {
        name: "Gross Sales",
        type: "column",
        yAxis: 1,
        data: salesSeries,
        color: "#ffd043",
        dataLabels: {
          enabled: true,
          formatter: function () {
            return fmtMoney(this.y);
          },
          style: { fontWeight: "bold", color: "#000000", textOutline: "none" },
        },
      },
    ],

    credits: { enabled: false },
  };
}
